import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Building2, AlertTriangle, Activity, ShieldCheck, RefreshCw, Mail, Phone, Globe, User, Sliders } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { StatCard } from '../components/ui/StatCard';
import { SeverityBadge } from '../components/ui/SeverityBadge';
import { IncidentTable } from '../components/incidents/IncidentTable';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { SkeletonCard } from '../components/ui/Skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/Tabs';
import { TenantTuningSection } from '../components/tuning/TenantTuningSection';
import { getTenantById, getIncidents } from '../services';
import type { Severity, Tenant, EnrichedIncident } from '../data/types';
import { cn } from '../lib/utils';

type IncidentFilter = 'all' | 'active' | 'closed';

export function TenantOverview() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [incidents, setIncidents] = useState<EnrichedIncident[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<IncidentFilter>('active');

  const fetchData = useCallback(async () => {
    if (!tenantId) return;

    setIsLoading(true);
    setError(null);

    try {
      const [tenantResult, incidentsResult] = await Promise.all([
        getTenantById(tenantId),
        getIncidents({ tenants: [tenantId] }),
      ]);

      if (tenantResult.success && tenantResult.data) {
        setTenant(tenantResult.data);
      } else {
        setError('Tenant not found');
        return;
      }

      if (incidentsResult.success) {
        setIncidents(incidentsResult.data);
      }
    } catch (err) {
      setError('Failed to connect to API');
      console.error('Error fetching tenant data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!tenantId) {
    return <NotFound />;
  }

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Link
          to="/tenants"
          className="inline-flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Customers</span>
        </Link>
        <SkeletonCard />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
        <SkeletonCard />
      </div>
    );
  }

  if (error || !tenant) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Link
          to="/tenants"
          className="inline-flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to Customers</span>
        </Link>
        {error === 'Tenant not found' ? (
          <NotFound />
        ) : (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-red-700 dark:text-red-300 mb-2">Failed to load tenant</h3>
            <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
            <Button onClick={fetchData}>
              <RefreshCw className="w-4 h-4" />
              Retry
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Calculate stats
  const severityCounts = incidents.reduce(
    (acc, inc) => {
      acc[inc.severity] = (acc[inc.severity] || 0) + 1;
      return acc;
    },
    {} as Record<Severity, number>
  );

  const activeCount = incidents.filter((inc) => inc.platformStatus !== 'closed').length;
  const closedCount = incidents.filter((inc) => inc.platformStatus === 'closed').length;

  // Filter incidents based on selected filter
  const filteredIncidents = incidents.filter((inc) => {
    if (filter === 'all') return true;
    if (filter === 'active') return inc.platformStatus !== 'closed';
    if (filter === 'closed') return inc.platformStatus === 'closed';
    return true;
  });

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Back navigation */}
      <Link
        to="/tenants"
        className="inline-flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span className="text-sm">Back to Customers</span>
      </Link>

      {/* Tenant header */}
      <div className="bg-bg-secondary rounded-xl border border-border-default p-6 shadow-sm">
        <div className="flex items-start justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-accent-blue/15">
              <Building2 className="w-8 h-8 text-accent-blue" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-text-primary mb-1">
                {tenant.displayName}
              </h1>
              <div className="flex items-center gap-3 text-sm text-text-secondary">
                <span className="font-mono">{tenant.tenantId}</span>
                <span className="text-text-muted">|</span>
                <Badge
                  variant={tenant.onboardingState === 'active' ? 'green' : 'yellow'}
                >
                  {tenant.onboardingState}
                </Badge>
              </div>
              {tenant.externalTenantId && (
                <div className="text-xs text-text-secondary font-mono mt-2">
                  External ID: {tenant.externalTenantId}
                </div>
              )}
            </div>
          </div>
          <Button variant="secondary" onClick={fetchData}>
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tenant Contact Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Contact Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-bg-tertiary">
                <Mail className="w-4 h-4 text-text-secondary" />
              </div>
              <div>
                <div className="text-xs text-text-secondary">Primary Contact</div>
                <div className="text-sm font-medium text-text-primary">
                  contact@{tenant.displayName.toLowerCase().replace(/\s+/g, '')}.com
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-bg-tertiary">
                <Phone className="w-4 h-4 text-text-secondary" />
              </div>
              <div>
                <div className="text-xs text-text-secondary">Support Line</div>
                <div className="text-sm font-medium text-text-primary">
                  +1 (555) 000-0000
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-bg-tertiary">
                <Globe className="w-4 h-4 text-text-secondary" />
              </div>
              <div>
                <div className="text-xs text-text-secondary">Service Level</div>
                <div className="text-sm font-medium text-text-primary">
                  <Badge variant="blue">Standard</Badge>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          icon={<Activity className="w-5 h-5" />}
          label="Total Incidents"
          value={incidents.length}
          accentColor="text-accent-blue"
        />
        <StatCard
          icon={<AlertTriangle className="w-5 h-5" />}
          label="Active"
          value={activeCount}
          accentColor="text-orange-500"
        />
        <StatCard
          icon={<ShieldCheck className="w-5 h-5" />}
          label="Closed"
          value={closedCount}
          accentColor="text-green-500"
        />
        <StatCard
          icon={<AlertTriangle className="w-5 h-5" />}
          label="Critical/High"
          value={(severityCounts.critical || 0) + (severityCounts.high || 0)}
          accentColor="text-red-500"
        />
      </div>

      {/* Severity breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Severity Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            {(['critical', 'high', 'medium', 'low', 'informational'] as Severity[]).map(
              (severity) => (
                <div key={severity} className="flex items-center gap-2">
                  <SeverityBadge severity={severity} showPulse={false} />
                  <span className="text-lg font-mono font-bold text-text-primary">
                    {severityCounts[severity] || 0}
                  </span>
                </div>
              )
            )}
          </div>
        </CardContent>
      </Card>

      {/* Main content tabs */}
      <Tabs defaultValue="incidents">
        <TabsList>
          <TabsTrigger value="incidents" icon={<Activity className="w-3.5 h-3.5" />}>
            Incidents ({incidents.length})
          </TabsTrigger>
          <TabsTrigger value="tuning" icon={<Sliders className="w-3.5 h-3.5" />}>
            Tuning
          </TabsTrigger>
        </TabsList>

        <TabsContent value="incidents">
          <Card padding="none">
            <CardHeader className="px-4 pt-4">
              <div className="flex items-center justify-between">
                <CardTitle>Incidents ({filteredIncidents.length})</CardTitle>
                <div className="flex items-center gap-1 p-1 bg-bg-tertiary rounded-lg">
                  {(['all', 'active', 'closed'] as IncidentFilter[]).map((f) => (
                    <button
                      key={f}
                      onClick={() => setFilter(f)}
                      className={cn(
                        'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                        filter === f
                          ? 'bg-bg-secondary text-text-primary shadow-sm'
                          : 'text-text-secondary hover:text-text-primary'
                      )}
                    >
                      {f === 'all' && `All (${incidents.length})`}
                      {f === 'active' && `Active (${activeCount})`}
                      {f === 'closed' && `Closed (${closedCount})`}
                    </button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-0">
              {filteredIncidents.length > 0 ? (
                <IncidentTable incidents={filteredIncidents} />
              ) : (
                <div className="p-8 text-center text-text-secondary">
                  {filter === 'all'
                    ? 'No incidents found for this tenant.'
                    : `No ${filter} incidents found for this tenant.`}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tuning">
          <TenantTuningSection tenantId={tenantId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function NotFound() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center">
        <Building2 className="w-16 h-16 text-amber-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-text-primary mb-2">Tenant Not Found</h2>
        <p className="text-text-secondary mb-4">
          The tenant you're looking for doesn't exist or has been removed.
        </p>
        <Link to="/tenants">
          <Button>
            <ArrowLeft className="w-4 h-4" />
            Back to Customers
          </Button>
        </Link>
      </div>
    </div>
  );
}
